<template>
  <div>
    <div
        @click="isExpanded = false"
        class="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
        :class="isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'"
    ></div>

    <div
        class="fixed bottom-0 left-0 right-0 h-[80px] bg-[var(--card-bg-color)] border-t border-[var(--border-color)] flex items-center justify-between px-4 z-50 transition-transform duration-500 ease-in-out"
        :class="{ 'translate-y-[80px]': isExpanded }"
        @click.self="isExpanded = true"
    >
      <div @click="isExpanded = true" class="flex items-center gap-3 cursor-pointer overflow-hidden">
        <i class="fa-solid fa-chevron-up text-gray-400"></i>
        <div class="min-w-0">
          <p class="font-bold text-sm truncate">{{ currentChapterName }}</p>
          <p class="text-xs text-gray-400 truncate">{{ story.name }}</p>
        </div>
      </div>
      <button @click.stop="togglePlayPause" class="text-2xl w-12 h-12 rounded-full bg-blue-600 text-white flex-shrink-0 flex items-center justify-center shadow-lg hover:bg-blue-700 transition">
        <i :class="{ 'fa-solid fa-spinner fa-spin': isLoading, 'fa-solid fa-pause': isPlaying && !isLoading, 'fa-solid fa-play': !isPlaying && !isLoading }"></i>
      </button>
    </div>

    <div
        @transitionend="onExpandTransitionEnd"
        class="fixed inset-0 player-expanded-view z-50 transition-transform duration-500 ease-in-out flex flex-col"
        :class="{ 'translate-y-full': !isExpanded }">
      <header class="flex-shrink-0 flex items-center justify-between p-4 border-b border-[var(--border-color)]">
        <router-link to="/" @click="isExpanded = false" class="text-xl font-bold tracking-wider hover:text-blue-500 transition-colors">
          TruyenAudio
        </router-link>
        <button @click="isExpanded = false" class="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white transition">
          <i class="fa-solid fa-chevron-down text-2xl"></i>
        </button>
      </header>

      <div class="flex-shrink-0 px-4 pt-6 pb-2 text-center">
        <h2 class="text-2xl font-bold">{{ story.name }}</h2>
        <p class="text-md text-gray-400 mt-1">Tác giả: {{ story.author }}</p>
      </div>

      <div class="flex-shrink-0 flex justify-between items-center px-4 py-2 border-b border-[var(--border-color)]">
        <span class="text-sm font-bold text-gray-300">Tiếp theo</span>
        <span class="text-xs text-gray-400">{{ story.chapters.length }} chương</span>
      </div>

      <div class="flex-grow overflow-y-auto px-2">
        <ChapterList
            ref="chapterList"
            :chapters="story.chapters"
            :current-chapter-index="currentChapterIndex"
            @play-chapter="playChapterFromList"
        />
      </div>

      <div class="flex-shrink-0 p-4 space-y-3 bg-[var(--bg-color)] border-t border-[var(--border-color)]">
        <div class="text-center mb-3">
          <p class="text-sm text-gray-400">{{ currentChapterName }}</p>
        </div>
        <div class="w-full flex items-center space-x-2">
          <span class="time-display">{{ formattedCurrentTime }}</span>
          <div @click="seek" class="relative w-full h-4 group flex items-center cursor-pointer">
            <div class="w-full h-1.5 bg-gray-600 rounded-full">
              <div class="h-full bg-white rounded-full" :style="{ width: progressBarWidth }"></div>
            </div>
          </div>
          <span class="time-display">{{ formattedTotalTime }}</span>
        </div>
        <div class="flex items-center justify-center w-full">
          <div class="flex-1"></div>

          <div class="flex items-center justify-center space-x-6">
            <button @click="prevTrack" class="control-btn"><i class="fa-solid fa-backward-step"></i></button>
            <button @click="rewind" class="control-btn"><i class="fa-solid fa-rotate-left"></i></button>
            <button @click="togglePlayPause" class="text-4xl w-16 h-16 rounded-full bg-white text-black flex items-center justify-center shadow-lg transform hover:scale-105 transition" :disabled="isLoading">
              <i :class="{ 'fa-solid fa-spinner fa-spin': isLoading, 'fa-solid fa-pause': isPlaying && !isLoading, 'fa-solid fa-play': !isPlaying && !isLoading }"></i>
            </button>
            <button @click="forward" class="control-btn"><i class="fa-solid fa-rotate-right"></i></button>
            <button @click="nextTrack" class="control-btn"><i class="fa-solid fa-forward-step"></i></button>
          </div>

          <div class="flex-1 flex justify-end">
            <div class="relative">
              <select v-model="playbackRate" class="speed-selector">
                <option value="1">1.0x</option>
                <option value="1.25">1.25x</option>
                <option value="1.5">1.5x</option>
                <option value="1.75">1.75x</option>
                <option value="2">2.0x</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    <audio ref="audioPlayer" class="hidden" @timeupdate="updateProgress" @loadedmetadata="updateTotalTime" @ended="nextTrack" @play="isPlaying = true" @pause="isPlaying = false" @canplay="onCanPlay"></audio>
  </div>
</template>

<script>
import {inject} from 'vue';
import ChapterList from '@/components/ChapterList.vue';

export default {
  name: 'AudioPlayer',
  components: {
    ChapterList
  },
  setup() {
    const audioState = inject('audioState');
    return {audioState};
  },
  data() {
    return {
      isExpanded: false,
      audio: null,
      isPlaying: false,
      isLoading: false,
      currentTime: 0,
      duration: 0,
      currentObjectUrl: null,
      playbackRate: 1,
      isTransitioning: false,
      preloadedChapter: {
        index: -1,
        objectUrl: null,
      },
    }
  },
  computed: {
    story() {
      return this.audioState.story;
    },
    currentChapterIndex() {
      return this.audioState.currentChapterIndex;
    },
    currentChapterName() {
      return this.currentChapterIndex > -1 ? this.story.chapters[this.currentChapterIndex] : 'Chưa chọn chương';
    },
    formattedCurrentTime() {
      return this.formatTime(this.currentTime);
    },
    formattedTotalTime() {
      return this.formatTime(this.duration);
    },
    progressBarWidth() {
      return this.duration ? `${(this.currentTime / this.duration) * 100}%` : '0%';
    }
  },
  watch: {
    story(newStory, oldStory) {
      if (newStory && (!oldStory || newStory.slug !== oldStory.slug)) {
        this.isExpanded = true;
      }
    },
    currentChapterIndex: {
      handler(newIndex) {
        if (newIndex > -1) this.playChapterByIndex(newIndex);
      },
      immediate: true
    },
    playbackRate(newRate) {
      if (this.audio) this.audio.playbackRate = newRate;
      localStorage.setItem('audio-playback-rate', newRate);
    },
    currentChapterName() {
      this.updateMetadata();
    },
    isPlaying() {
      this.updateMetadata();
    }
  },
  methods: {
    onExpandTransitionEnd() {
      if (this.isExpanded && this.$refs.chapterList) {
        this.$refs.chapterList.scrollToActive();
      }
    },
    updateGlobalHistory() {
      if (!this.story) return;
      const history = JSON.parse(localStorage.getItem('full-audio-history') || '{}');
      history[this.story.slug] = {
        storyName: this.story.name,
        author: this.story.author,
        chapterIndex: this.currentChapterIndex,
        chapterName: this.currentChapterName,
        timestamp: new Date().getTime(),
        slug: this.story.slug,
      };
      localStorage.setItem('full-audio-history', JSON.stringify(history));
    },
    playChapterFromList(index) {
      this.audioState.setTrack(this.story, index);
    },
    async playChapterByIndex(index) {
      if (!this.story || index < 0 || index >= this.story.chapters.length) return;

      this.isTransitioning = true;
      this.isLoading = true;
      this.isPlaying = false;
      this.currentTime = 0;
      this.duration = 0;

      if (this.preloadedChapter.index === index && this.preloadedChapter.objectUrl) {
        if (this.currentObjectUrl) URL.revokeObjectURL(this.currentObjectUrl);
        this.currentObjectUrl = this.preloadedChapter.objectUrl;

        this.preloadedChapter.index = -1;
        this.preloadedChapter.objectUrl = null;

      } else {
        const chapterName = this.story.chapters[index];
        const audioUrl = `${this.story.deployment_url}/${encodeURIComponent(chapterName)}.ogg`;
        try {
          const response = await fetch(audioUrl);
          if (!response.ok) throw new Error(`Lỗi mạng: ${response.statusText}`);
          const audioBlob = await response.blob();
          if (this.currentObjectUrl) URL.revokeObjectURL(this.currentObjectUrl);
          this.currentObjectUrl = URL.createObjectURL(audioBlob);
        } catch (error) {
          console.error("Lỗi khi tải chương:", error);
          this.isLoading = false;
          this.isTransitioning = false;
          return;
        }
      }

      this.audio.src = this.currentObjectUrl;
      this.audio.load();

      const history = this.loadHistory(this.story.slug);
      if (history && history.chapterIndex === index && isFinite(history.currentTime)) {
        this.audio.addEventListener('loadedmetadata', () => {
          if (history.currentTime < this.audio.duration) this.audio.currentTime = history.currentTime;
        }, {once: true});
      }
    },
    onCanPlay() {
      this.isLoading = false;
      this.isTransitioning = false;
      this.audio.play().catch(e => console.error("Lỗi khi phát audio:", e));
      this.updateGlobalHistory();
      this.preloadNextChapter();

    },
    async preloadNextChapter() {
      const nextIndex = this.currentChapterIndex + 1;
      if (nextIndex >= this.story.chapters.length) return;

      if (this.preloadedChapter.index === nextIndex) return;

      try {
        const chapterName = this.story.chapters[nextIndex];
        const audioUrl = `${this.story.deployment_url}/${encodeURIComponent(chapterName)}.ogg`;
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error('Lỗi tải file preload');
        const audioBlob = await response.blob();

        if (this.preloadedChapter.objectUrl) {
          URL.revokeObjectURL(this.preloadedChapter.objectUrl);
        }

        this.preloadedChapter = {
          index: nextIndex,
          objectUrl: URL.createObjectURL(audioBlob),
        };
      } catch (error) {
        console.error("Lỗi khi preload chapter tiếp theo:", error);
        this.preloadedChapter.index = -1;
        this.preloadedChapter.objectUrl = null;
      }
    },
    togglePlayPause() {
      if (this.isLoading) return;
      if (this.isPlaying) this.audio.pause();
      else {
        if (!this.audio.src) {
          if (this.currentChapterIndex > -1) this.playChapterByIndex(this.currentChapterIndex);
        } else {
          this.audio.play().catch(e => console.error("Lỗi khi phát audio:", e));
        }
      }
    },
    rewind() {
      this.audio.currentTime = Math.max(0, this.audio.currentTime - 10);
    },
    forward() {
      this.audio.currentTime = Math.min(this.duration, this.audio.currentTime + 10);
    },
    prevTrack() {
      if (this.currentChapterIndex > 0) {
        this.audioState.setTrack(this.story, this.currentChapterIndex - 1);
      }
    },
    nextTrack() {
      if (this.currentChapterIndex < this.story.chapters.length - 1) {
        this.audioState.setTrack(this.story, this.currentChapterIndex + 1);
      }
    },
    updateProgress(e) {
      if (this.isTransitioning) return;
      this.currentTime = e.target.currentTime;
      this.saveHistory(this.story.slug, this.currentChapterIndex, this.currentTime);
    },
    updateTotalTime(e) {
      this.duration = e.target.duration;
    },
    seek(e) {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      if (isFinite(this.audio.duration)) this.audio.currentTime = (clickX / width) * this.audio.duration;
    },
    formatTime(seconds) {
      const secs = Math.floor(seconds);
      if (isNaN(secs) || secs < 0) return '00:00';
      const minutes = Math.floor(secs / 60);
      const remainingSeconds = secs % 60;
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    saveHistory(slug, chapterIndex, currentTime) {
      if (slug && chapterIndex > -1 && isFinite(currentTime)) localStorage.setItem(`audio-history-${slug}`, JSON.stringify({chapterIndex, currentTime}));
    },
    loadHistory(slug) {
      const history = localStorage.getItem(`audio-history-${slug}`);
      try {
        return history ? JSON.parse(history) : null;
      } catch (e) {
        return null;
      }
    },
    updateMetadata() {
      if (!this.story) {
        document.title = 'TruyenAudio';
        return;
      }

      const statusIcon = this.isPlaying ? '▶' : '⏸';
      const title = `${statusIcon} ${this.currentChapterName} - ${this.story.name}`;
      document.title = title;

      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: this.currentChapterName,
          artist: this.story.author,
          album: this.story.name,
        });
        navigator.mediaSession.playbackState = this.isPlaying ? 'playing' : 'paused';
      }
    },
    setupMediaSessionHandlers() {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', () => this.togglePlayPause());
        navigator.mediaSession.setActionHandler('pause', () => this.togglePlayPause());
        navigator.mediaSession.setActionHandler('previoustrack', () => this.prevTrack());
        navigator.mediaSession.setActionHandler('nexttrack', () => this.nextTrack());
        navigator.mediaSession.setActionHandler('seekbackward', () => this.rewind());
        navigator.mediaSession.setActionHandler('seekforward', () => this.forward());
      }
    },
  },
  mounted() {
    this.isExpanded = true;

    this.audio = this.$refs.audioPlayer;
    const savedRate = localStorage.getItem('audio-playback-rate');
    if (savedRate) this.playbackRate = parseFloat(savedRate);
    this.audio.playbackRate = this.playbackRate;

    this.setupMediaSessionHandlers();
  },
  beforeUnmount() {
    if (this.currentObjectUrl) URL.revokeObjectURL(this.currentObjectUrl);
    if (this.preloadedChapter.objectUrl) {
      URL.revokeObjectURL(this.preloadedChapter.objectUrl);
    }
  }
}
</script>

<style scoped>
.player-expanded-view {
  background-color: var(--bg-color);
  box-shadow: 0 -10px 30px -15px rgba(0, 0, 0, 0.3);
}

.control-btn {
  @apply text-2xl text-gray-400 hover:text-white transition-colors duration-200;
}

.time-display {
  @apply text-xs w-14 text-center font-mono text-gray-400 flex-shrink-0;
}

.speed-selector {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  background-color: var(--card-footer-bg);
  border: 1px solid var(--border-color);
  color: var(--text-muted-color);
  @apply text-sm font-semibold rounded-md py-2 pl-3 pr-10 cursor-pointer focus:outline-none hover:text-white transition;
}
</style>
